import React, { FC, useEffect, useState, useDeferredValue, useContext } from 'react';
import { find, forEach, get, map } from 'lodash';
import { t, Trans } from '@lingui/macro';
import {
  Text,
  Box,
  SideSheet,
  SideSheetSize,
  ToggleSwitch,
  IconSize,
  Icon,
  TextFieldSize,
  SideSheetContentCol,
  SpinnerSize,
  Spinner,
  Heading,
} from '@bechtel/gds';
import { CacheKeys, ErrorRetry, useSessionStorage, ErrorBoundary } from '@bechtel/client-common';
import {
  CloseStyled,
  HeaderStyled,
  SearchFieldComponentStyled,
  SearchFieldStyled,
  StencilListStyled,
  ListStyled,
  SideSheetBodyStyled,
} from './styled';

import { StencilCloseRequestStatus } from './stencil-close-request-status';
import { CloseRodRequestStatus } from './close-rod-request-status/internals';
import { useLazyQuery } from '@apollo/client';
import { ROD_REQUEST_STATS_BY_WELDER_AND_ROD_ROOM_NAME, GET_ROD_REQUESTS_BY_WELDER_ID } from '@bechtel/client-gql';
import {
  PagingInput,
  RodRequestStatsByCrewMemberInput,
  RodRequestStatsList,
  FilterOperator,
  RodRequestStatsByCrewMemberFilterInput,
  RodRequestStatsByCrewMemberFilterByTerm,
  RodRequestFilterInput,
  RodRequestSortByTerm,
  SortOrder,
  RodRequestComplexFilterInput,
  RodRequestFilterByTerm,
  RodRequestStatusCode,
  RodRequestList,
  RodRequest,
  RodRoom,
} from '@bechtel/types';

import { StencilStats, StencilRodRequests, StencilClosedRodRequestIds } from './types';
import { RodRoomDataContext } from '~/utilities/context';

interface CloseBulkRequestsProps {
  onClose: () => void;
}

export const CloseBulkRequests: FC<CloseBulkRequestsProps> = ({ onClose }) => {
  const pageLimit = 20;
  const [showAllRooms, setShowAllRooms] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDeferredValue(searchQuery);
  const { selectedRodRoom } = useContext(RodRoomDataContext);
  const [stencilId, setStencilId] = useState(null);
  const [statsCount, setStatsCount] = useState<StencilStats>();
  const [rodRequests, setRodRequests] = useState<StencilRodRequests>();
  const [closedRodRequestIds, setClosedRodRequestIds] = useState<StencilClosedRodRequestIds>();

  const onViewStencilId = (stencilId) => {
    setStencilId(stencilId);
  };
  const [
    loadWelderStats,
    {
      loading: welderStatsLoading,
      data: welderStatsData,
      error: welderStatsError,
      refetch: welderStatsRefetch,
      fetchMore: welderStatsFetchMore,
      called: welderStatsCalled,
    },
  ] = useLazyQuery<
    { rodRequestStatsList: RodRequestStatsList },
    {
      input: RodRequestStatsByCrewMemberInput;
      paging: PagingInput;
    }
  >(ROD_REQUEST_STATS_BY_WELDER_AND_ROD_ROOM_NAME, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const [
    getRodRequests,
    {
      loading: rodRequestLoading,
      data: rodRequestData,
      error: rodRequestError,
      refetch: rodRequestRefetch,
      called: rodRequestCalled,
    },
  ] = useLazyQuery<
    {
      rodRequests: RodRequestList;
    },
    {
      input: RodRequestFilterInput;
      paging?: PagingInput;
    }
  >(GET_ROD_REQUESTS_BY_WELDER_ID, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'no-cache',
  });
  useEffect(() => {
    if (debouncedSearchQuery.length > 0 && debouncedSearchQuery.length < 3) return;
    const rodRequestStatsByCrewMemberFilter: RodRequestStatsByCrewMemberFilterInput = {
      and: [],
    };
    if (debouncedSearchQuery)
      rodRequestStatsByCrewMemberFilter.and?.push({
        term: RodRequestStatsByCrewMemberFilterByTerm.CREW_MEMBER_NAME,
        value: debouncedSearchQuery,
        operator: FilterOperator.FUZZY,
      });

    if (!showAllRooms)
      rodRequestStatsByCrewMemberFilter.and?.push({
        term: RodRequestStatsByCrewMemberFilterByTerm.ROD_ROOM_ID,
        value: selectedRodRoom?.id,
        operator: FilterOperator.EQUALS,
      });

    rodRequestStatsByCrewMemberFilter.and?.push({
      term: RodRequestStatsByCrewMemberFilterByTerm.STENCIL_ROD_REQUEST_STATUS_ID,
      value: RodRequestStatusCode.REQUESTED,
      operator: FilterOperator.EQUALS,
    });

    loadWelderStats({
      variables: {
        input: {
          complexFilter: rodRequestStatsByCrewMemberFilter,
        },
        paging: {
          limit: pageLimit,
          cursor: '1',
        },
      },
    });
  }, [loadWelderStats, debouncedSearchQuery, showAllRooms]);
  const totalWelders = get(welderStatsData, 'rodRequestStatsByCrewMember.total');
  const rodRequestWeldersStatsList = get(welderStatsData, 'rodRequestStatsByCrewMember.items');
  const totalPages = totalWelders ? Math.ceil(totalWelders / pageLimit) : 0;
  const weldersListCursor = get(welderStatsData, 'rodRequestStatsByCrewMember.cursor', 1);
  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      weldersListCursor <= totalPages &&
      !welderStatsLoading
    ) {
      welderStatsFetchMore({
        variables: {
          paging: {
            cursor: weldersListCursor.toString(),
            limit: pageLimit,
          },
        },
      });
    }
  };

  const getRodRequestFilterInput = (stencilId) => {
    const rodRequestFilterInput: RodRequestFilterInput = {
      sortByFilter: {
        term: RodRequestSortByTerm.REQUEST_TIME,
        sortOrder: SortOrder.DESC,
      },
    };
    const rodRequestComplexFilter: RodRequestComplexFilterInput = {
      and: [
        {
          term: RodRequestFilterByTerm.ASSIGNEE_ID,
          value: stencilId,
          operator: FilterOperator.EQUALS,
        },
        {
          term: RodRequestFilterByTerm.STENCIL_ROD_REQUEST_STATUS_ID,
          value: RodRequestStatusCode.REQUESTED,
          operator: FilterOperator.EQUALS,
        },
      ],
      or: [],
    };
    rodRequestFilterInput.complexFilter = rodRequestComplexFilter;
    return rodRequestFilterInput;
  };

  const rodRequestList: RodRequest[] = get(rodRequestData, 'rodRequests.items', []);

  useEffect(() => {
    if (
      stencilId &&
      !(rodRequests && rodRequests[stencilId]) &&
      rodRequestWeldersStatsList &&
      rodRequestWeldersStatsList.length > 0
    )
      getRodRequests({
        variables: {
          input: getRodRequestFilterInput(stencilId),
          paging: {
            cursor: '1',
            limit: pageLimit,
          },
        },
      });
  }, [stencilId, getRodRequests, rodRequestWeldersStatsList, showAllRooms]);

  useEffect(() => {
    if (rodRequestWeldersStatsList && rodRequestWeldersStatsList.length > 0) {
      setStencilId(rodRequestWeldersStatsList[0].crewMember.id);
      const statsObj = {};
      forEach(rodRequestWeldersStatsList, (crewMemberStats) => {
        statsObj[crewMemberStats.crewMember.id] = {
          closed: 0,
          open: get(
            find(
              crewMemberStats.rodRequestStatusStats,
              (rodRequestStatusStat) => rodRequestStatusStat.status === RodRequestStatusCode.REQUESTED
            ),
            'stats'
          ),
        };
      });
      setStatsCount({ ...statsCount, ...statsObj });
    }
  }, [showAllRooms, rodRequestWeldersStatsList]);

  useEffect(() => {
    if (stencilId)
      setRodRequests({
        ...rodRequests,
        [stencilId]: {
          items:
            !showAllRooms && selectedRodRoom
              ? rodRequestList.filter((rodrequest) => rodrequest.rodRoom.id == selectedRodRoom?.id)
              : rodRequestList,
        },
      });
  }, [rodRequestList, showAllRooms]);
  const onClosedRodRequest = (id: RodRequest['id']) => {
    if (stencilId) {
      if (closedRodRequestIds && closedRodRequestIds[stencilId]) {
        const closedRodIds = closedRodRequestIds[stencilId]?.ids;
        closedRodIds.push(id);
        setClosedRodRequestIds({
          ...closedRodRequestIds,
          [stencilId]: { ids: closedRodIds },
        });
      } else {
        setClosedRodRequestIds({
          ...closedRodRequestIds,
          [stencilId]: {
            ids: [id],
          },
        });
      }

      setStatsCount({
        ...statsCount,
        [stencilId]: {
          closed: statsCount && statsCount[stencilId] ? statsCount[stencilId]?.closed + 1 : 0,
          open: statsCount && statsCount[stencilId] ? statsCount[stencilId]?.open : 0,
        },
      });
    }
  };

  return (
    <>
      <SideSheet onDismiss={onClose} visible size={SideSheetSize.MEDIUM}>
        <HeaderStyled title="" data-testid="header-container">
          <Box justifyContent={{ sm: 'space-between' }} pl={{ sm: '200' }}>
            <Box alignItems={{ sm: 'center' }}>
              <Icon icon="check-double" size={IconSize.MEDIUM} fillColor="green.500" />
              <Box pl={{ sm: '400' }} pr={{ sm: '550' }}>
                <Text color="grey.1000" weight="600" size="600">
                  <Trans>Bulk Close</Trans>
                </Text>
              </Box>
              <Box pl={{ sm: '550' }} alignItems={{ sm: 'center' }}>
                <ToggleSwitch
                  isToggled={showAllRooms}
                  onToggle={() => {
                    setStencilId(null);
                    setRodRequests({});
                    setStatsCount({});
                    setShowAllRooms(!showAllRooms);
                  }}
                />
                <Box pl={{ sm: '300' }}>
                  <Text color="bechtel.blue.700" size="400">
                    <Trans>All Rod Rooms</Trans>
                  </Text>
                </Box>
              </Box>
            </Box>
            <CloseStyled data-testid="closed" onClick={() => onClose()} />
          </Box>
        </HeaderStyled>
        <SideSheetBodyStyled>
          <StencilListStyled>
            <SearchFieldStyled data-testid="search-field">
              <SearchFieldComponentStyled
                size={TextFieldSize.MEDIUM}
                iconSize={IconSize.MEDIUM}
                placeholder={t`Search Welders`}
                type="search"
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                }}
                value={searchQuery}
              />
            </SearchFieldStyled>
            <SideSheetContentCol>
              <ListStyled
                onScroll={(e) => {
                  handleScroll(e);
                }}
              >
                {!welderStatsLoading && welderStatsError && (
                  <Box data-testid="retry">
                    <ErrorRetry refetch={welderStatsRefetch}>
                      <Trans>There was a problem loading welder. Please click the button below to try again.</Trans>
                    </ErrorRetry>
                  </Box>
                )}

                {!welderStatsError &&
                  map(rodRequestWeldersStatsList, (crewMemberStats, index) => {
                    return (
                      <ErrorBoundary>
                        <StencilCloseRequestStatus
                          key={crewMemberStats.crewMember.id}
                          index={index}
                          rodRequestCrewMemberStatsItem={crewMemberStats}
                          onViewStencilId={onViewStencilId}
                          welderStatsCount={statsCount}
                          stencilId={stencilId}
                        />
                      </ErrorBoundary>
                    );
                  })}
                {welderStatsCalled &&
                  !welderStatsLoading &&
                  !welderStatsError &&
                  rodRequestWeldersStatsList?.length === 0 && (
                    <Box
                      flexDirection={{
                        sm: 'column',
                      }}
                      alignItems={{
                        sm: 'center',
                      }}
                      pt={{ sm: '900' }}
                    >
                      <Heading
                        size="400"
                        weight="600"
                        color="grey.600"
                        style={{
                          textAlign: 'center',
                          lineHeight: '1.57',
                        }}
                      >
                        <Trans> Stencil Not Found With Open Rod Request.</Trans>
                      </Heading>
                      <Box mt={{ sm: '700' }} mb={{ sm: '600' }} px={{ sm: '600' }}>
                        <Text
                          size="400"
                          family="graphik"
                          color="grey.600"
                          style={{
                            textAlign: 'center',
                            lineHeight: '1.57',
                          }}
                        >
                          <Trans>Try changing your search criteria or rod room.</Trans>
                        </Text>
                      </Box>
                    </Box>
                  )}

                {welderStatsLoading && (
                  <Box
                    data-testid="welder-stats-loading"
                    p={{ sm: '500' }}
                    alignItems={{ sm: 'center' }}
                    justifyContent={{ sm: 'center' }}
                    flexGrow={{ sm: 1 }}
                    css={rodRequestWeldersStatsList && rodRequestWeldersStatsList.length === 0 ? 'height: 100%;' : ''}
                  >
                    <Spinner size={SpinnerSize.LARGE} />
                  </Box>
                )}
              </ListStyled>
            </SideSheetContentCol>
          </StencilListStyled>
          <SideSheetContentCol data-testid="bulk-rod-request-list">
            <Box flexDirection={{ sm: 'column' }} p={{ sm: '500' }} data-testid="close-rod-request-status">
              {!rodRequestLoading && rodRequestError && (
                <Box data-testid="retry">
                  <ErrorRetry refetch={rodRequestRefetch}>
                    <Trans>There was a problem loading Rod Request. Please click the button below to try again.</Trans>
                  </ErrorRetry>
                </Box>
              )}
              {!rodRequestError &&
                rodRequestWeldersStatsList &&
                !!rodRequestWeldersStatsList.length &&
                rodRequests &&
                stencilId &&
                rodRequests[stencilId] &&
                map(rodRequests[stencilId].items, (rodRequest) => {
                  return (
                    <ErrorBoundary>
                      <CloseRodRequestStatus
                        rodRequest={rodRequest}
                        key={`${rodRequest.id}.${stencilId}`}
                        stencilId={stencilId}
                        onClosedRodRequest={onClosedRodRequest}
                        closedRodRequestIds={closedRodRequestIds ? closedRodRequestIds[stencilId]?.ids : []}
                      />
                    </ErrorBoundary>
                  );
                })}
              {rodRequestLoading && (
                <Box
                  p={{ sm: '500' }}
                  alignItems={{ sm: 'center' }}
                  justifyContent={{ sm: 'center' }}
                  flexGrow={{ sm: 1 }}
                  css={rodRequestList && rodRequestList.length === 0 ? 'height: 100%;' : ''}
                >
                  <Spinner size={SpinnerSize.LARGE} />
                </Box>
              )}
              {rodRequestCalled &&
                !rodRequestLoading &&
                !rodRequestError &&
                rodRequestList &&
                rodRequestList.length === 0 && (
                  <Box
                    alignItems={{
                      sm: 'center',
                    }}
                    justifyContent={{ sm: 'center' }}
                    pt={{ sm: '900' }}
                  >
                    <Heading
                      size="400"
                      weight="600"
                      color="grey.600"
                      style={{
                        textAlign: 'center',
                      }}
                    >
                      <Trans> No Rod Request.</Trans>
                    </Heading>
                  </Box>
                )}
            </Box>
          </SideSheetContentCol>
        </SideSheetBodyStyled>
      </SideSheet>
    </>
  );
};
